import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSystemStatus, type SystemStatusSnapshot } from '@clarix/api-client';
import { hasInternet } from '@clarix/utils';
import { env } from '@/config/env';
import { MaintenancePage } from './MaintenancePage';

interface AppStatusGateProps {
  children: React.ReactNode;
}

/** Retry a few times so a single status blip doesn't read as a full outage. */
const STATUS_RETRY_ATTEMPTS = 3;
const STATUS_RETRY_DELAY_MS = 800;

/** How often to re-probe while a mid-session outage is on screen. */
const RECOVERY_POLL_MS = 5_000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchStatusWithRetry(baseURL: string): Promise<SystemStatusSnapshot> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= STATUS_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await fetchSystemStatus(baseURL);
    } catch (err) {
      lastErr = err;
      if (attempt < STATUS_RETRY_ATTEMPTS) await delay(STATUS_RETRY_DELAY_MS);
    }
  }
  throw lastErr;
}

type Gate =
  | { kind: 'checking' }
  | { kind: 'ok' }
  | { kind: 'maintenance'; status: SystemStatusSnapshot }
  | { kind: 'unreachable' }
  /** Went down AFTER boot — overlaid on the live app, never a remount. */
  | { kind: 'live-outage'; status?: SystemStatusSnapshot };

/**
 * System-status gate — the shell's single source of truth for "is the platform
 * up", at boot AND for the rest of the session.
 *
 * BOOT: calls GET /system/status (with a short retry) before the route tree
 * renders, and resolves to one of:
 *
 *  - `{ maintenance: true }`  → full-screen {@link MaintenancePage} (planned
 *    maintenance; surfaces the operator's message/eta).
 *  - backend unreachable while the INTERNET works → the same maintenance page with a
 *    "we can't reach Clarix" message + Check again. The backend being down must
 *    not fall through to a normal-looking app (e.g. the "already signed in"
 *    login card) that then breaks on the first real request.
 *  - backend unreachable and NO INTERNET → boot normally. "Offline" is decided
 *    by a real reachability probe ({@link hasInternet}), not `navigator.onLine`,
 *    which stays true on a wifi with no internet. A dropped connection is
 *    the user's own; the thin OfflineBanner strip signals it — a full lockout
 *    for your own wifi would be wrong, and everything recovers on reconnect.
 *  - status ok → render children.
 *
 * The retry means a genuine backend outage shows the page, while a one-off blip
 * that recovers within ~1.6s boots the app normally.
 *
 * AFTER BOOT: a one-shot check would leave the app claiming to be healthy for as
 * long as the tab stays open. So we re-probe whenever the tab becomes visible
 * again or the browser comes back online, and on a confirmed outage LAYER the
 * maintenance page over the running app (it is `fixed inset-0`) instead of
 * swapping the tree — nothing unmounts, so no in-flight work or unsaved input is
 * thrown away by a false positive. While it's up we keep polling, and the moment
 * the platform answers healthy the overlay disappears and the user is back
 * exactly where they were. Signed-in users get the same verdict from
 * WorkspaceShell's /me heartbeat; this covers the signed-out and idle-tab cases.
 */
export function AppStatusGate({ children }: AppStatusGateProps) {
  const [gate, setGate] = useState<Gate>({ kind: 'checking' });
  // One probe at a time — a focus event mid-probe must not stack another.
  const probingRef = useRef(false);

  useEffect(() => {
    let active = true;
    fetchStatusWithRetry(env.VITE_API_BASE_URL)
      .then((status) => {
        if (!active) return;
        setGate(status.maintenance ? { kind: 'maintenance', status } : { kind: 'ok' });
      })
      .catch(async () => {
        // Backend unreachable after retries. No internet → boot normally (the
        // user's own connection; the OfflineBanner strip signals it). Internet
        // works → the backend is down, so show the maintenance page instead of
        // a normal-looking app that breaks on the first request.
        const online = await hasInternet();
        if (!active) return;
        setGate(online ? { kind: 'unreachable' } : { kind: 'ok' });
      });
    return () => {
      active = false;
    };
  }, []);

  /** Re-probe and move the gate between `ok` and `live-outage`. */
  const probeLive = useCallback(async () => {
    if (probingRef.current) return;
    probingRef.current = true;
    try {
      const status = await fetchStatusWithRetry(env.VITE_API_BASE_URL);
      setGate(status.maintenance ? { kind: 'live-outage', status } : { kind: 'ok' });
    } catch {
      // Unreachable. No internet is the user's own connection — the
      // OfflineBanner owns that message, so leave the app alone.
      if (await hasInternet()) setGate({ kind: 'live-outage' });
    } finally {
      probingRef.current = false;
    }
  }, []);

  // Healthy → watch for the platform going down while the tab is open.
  const watching = gate.kind === 'ok';
  useEffect(() => {
    if (!watching) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void probeLive();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [watching, probeLive]);

  // Mid-session outage on screen → keep polling so it clears itself.
  const recovering = gate.kind === 'live-outage';
  useEffect(() => {
    if (!recovering) return undefined;
    const id = window.setInterval(() => void probeLive(), RECOVERY_POLL_MS);
    return () => window.clearInterval(id);
  }, [recovering, probeLive]);

  // "Check again" reloads — re-runs this gate from scratch.
  if (gate.kind === 'checking') return null;

  if (gate.kind === 'maintenance') {
    return <MaintenancePage message={gate.status.message} eta={gate.status.eta} />;
  }

  if (gate.kind === 'unreachable') {
    return (
      <MaintenancePage message="We can’t reach Clarix right now. This is usually temporary — please check again in a moment." />
    );
  }

  if (gate.kind === 'live-outage') {
    // Overlay, not a swap: the app underneath stays mounted so recovery costs
    // nothing. `onRetry` probes immediately instead of reloading the page.
    return (
      <>
        {children}
        <MaintenancePage
          message={gate.status?.message}
          eta={gate.status?.eta}
          onRetry={() => void probeLive()}
        />
      </>
    );
  }

  return <>{children}</>;
}
